import { createServerFn } from "@tanstack/react-start";
import { TRACKING_KEYS } from "@/lib/tracking-params";

// Hand-rolled validator (same rules as the previous zod schema). The input
// validator is bundled into the client, so avoiding zod here keeps ~50KB of
// JavaScript off every page.
type LeadPayload = {
  fullName: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  propertyType: "Home" | "Business";
  services: string[];
  timeline: string;
  message: string;
  bundleDiscount: boolean;
  serviceCount: number;
  plan: string;
  eventId: string;
  outOfArea: boolean;
  tracking: Record<string, string>;
  turnstileToken: string;
};

// Cloudflare Turnstile verification. Fail-open: any network/config problem
// lets the lead through — a real customer must never be blocked by our own
// bot check. Only a token that Cloudflare explicitly rejects is treated as spam.
async function verifyTurnstile(
  token: string,
  ip: string,
): Promise<{ ok: boolean; reason: string }> {
  const secret = process.env["TURNSTILE_SECRET_KEY"];
  if (!secret) return { ok: true, reason: "not-configured" };
  if (!token) return { ok: true, reason: "no-token" };
  try {
    const body = new URLSearchParams({ secret, response: token });
    if (ip) body.set("remoteip", ip);
    const res = await fetch(
      "https://challenges.cloudflare.com/turnstile/v0/siteverify",
      { method: "POST", body },
    );
    if (!res.ok) return { ok: true, reason: `http-${res.status}` };
    const json = (await res.json()) as {
      success?: boolean;
      "error-codes"?: string[];
    };
    if (json.success) return { ok: true, reason: "verified" };
    return { ok: false, reason: (json["error-codes"] ?? []).join(",") || "failed" };
  } catch {
    return { ok: true, reason: "verify-error" };
  }
}

const str = (v: unknown, max: number, min = 0, field = "field"): string => {
  const s = typeof v === "string" ? v.trim() : "";
  if (s.length < min) throw new Error(`Invalid ${field}`);
  return s.slice(0, max);
};

function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0] ?? "", lastName: "" };
  return { firstName: parts[0] ?? "", lastName: parts.slice(1).join(" ") };
}

const parsePayload = (input: unknown): LeadPayload => {
  const o = (input ?? {}) as Record<string, unknown>;
  const email = str(o["email"], 255, 0, "email");
  if (email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error("Invalid email");
  const propertyType = o["propertyType"] === "Business" ? "Business" : "Home";
  const services = Array.isArray(o["services"])
    ? o["services"].slice(0, 20).map((s) => String(s).slice(0, 60))
    : [];
  const TRACKING_FIELDS = TRACKING_KEYS as string[];
  const rawTracking = (o["tracking"] ?? {}) as Record<string, unknown>;
  const tracking: Record<string, string> = {};
  for (const key of TRACKING_FIELDS) tracking[key] = str(rawTracking[key], 500);
  return {
    fullName: str(o["fullName"], 200, 1, "full name"),
    phone: str(o["phone"], 20, 7, "phone"),
    email,
    address: str(o["address"], 200),
    city: str(o["city"], 100),
    state: str(o["state"], 50),
    zip: str(o["zip"], 20),
    propertyType,
    services,
    timeline: str(o["timeline"], 100),
    message: str(o["message"], 2000),
    bundleDiscount: o["bundleDiscount"] === true,
    plan: str(o["plan"], 60),
    serviceCount:
      typeof o["serviceCount"] === "number" && Number.isFinite(o["serviceCount"])
        ? Math.max(0, Math.min(20, Math.trunc(o["serviceCount"])))
        : services.length,
    eventId: str(o["eventId"], 100),
    outOfArea: o["outOfArea"] === true,
    tracking,
    turnstileToken: str(o["turnstileToken"], 3000),
  };
};

export const submitLead = createServerFn({ method: "POST" })
  .inputValidator(parsePayload)
  .handler(async ({ data }) => {
    // 0. Bot check (Cloudflare Turnstile) — fail-open, see verifyTurnstile.
    const { getRequestHeader } = await import("@tanstack/react-start/server");
    const ip =
      getRequestHeader("cf-connecting-ip") ||
      (getRequestHeader("x-forwarded-for") ?? "").split(",")[0]?.trim() ||
      "";
    const bot = await verifyTurnstile(data.turnstileToken, ip);
    if (!bot.ok) {
      console.warn("[submitLead] blocked by Turnstile:", bot.reason);
      return { ok: true };
    }

    // 1. Fire the GoHighLevel webhook IMMEDIATELY — no waiting on the DB.
    const webhookUrl = process.env.GHL_WEBHOOK_URL;
    let ghlError: string | null = null;

    const ghlPromise: Promise<boolean> = !webhookUrl
      ? (() => {
          ghlError = "GHL_WEBHOOK_URL not configured";
          console.error("[submitLead]", ghlError);
          return Promise.resolve(false);
        })()
      : fetch(webhookUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...splitFullName(data.fullName),
            phone: data.phone,
            email: data.email,
            address: data.address,
            city: data.city,
            state: data.state,
            zip: data.zip,
            propertyType: data.propertyType,
            services: data.services,
            timeline: data.timeline,
            message: data.outOfArea
              ? `[OUT OF AREA — 30+ mi from Albany] ${data.message}`.trim()
              : data.message,
            out_of_area: data.outOfArea,
            bundle_discount: data.bundleDiscount,
            service_count: data.serviceCount,
            plan: data.plan,
            membership_plan: data.plan,
            discount_note: data.bundleDiscount
              ? "10% multi-service discount promised on site"
              : "",
            source: data.tracking["source"] || "Website Quote",
            event_id: data.eventId,
            // Every captured attribution field (UTMs, gclid/gbraid/wbraid,
            // msclkid, fbclid/_fbp/_fbc, keyword, device, first-touch, journey…)
            ...data.tracking,
            // Underscore aliases — GHL/Meta CAPI field names use _fbp/_fbc.
            _fbp: data.tracking["fbp"] ?? "",
            _fbc: data.tracking["fbc"] ?? "",
            fbp: data.tracking["fbp"] ?? "",
            fbc: data.tracking["fbc"] ?? "",
            client_user_agent: data.tracking["user_agent"] ?? "",
            // Flattened attribution object some GHL workflows read directly.
            attribution: data.tracking,
          }),
        })
          .then(async (res) => {
            if (!res.ok) {
              ghlError = `GHL responded ${res.status}: ${await res
                .text()
                .catch(() => "")}`;
              console.error("[submitLead]", ghlError);
              return false;
            }
            return true;
          })
          .catch((err) => {
            ghlError = err instanceof Error ? err.message : String(err);
            console.error("[submitLead] GHL POST threw", err);
            return false;
          });

    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );

    const { firstName, lastName } = splitFullName(data.fullName);

    // 2. Save to leads table in parallel as backup — never lose a lead.
    const { data: inserted, error: insertErr } = await supabaseAdmin
      .from("leads" as never)
      .insert({
        first_name: firstName,
        last_name: lastName,
        phone: data.phone,
        email: data.email,
        address: data.address,
        city: data.city,
        state: data.state,
        zip: data.zip,
        property_type: data.propertyType,
        services: data.services,
        timeline: data.timeline,
        message: data.message,
        bundle_discount: data.bundleDiscount,
        service_count: data.serviceCount,
        plan: data.plan || null,
        out_of_area: data.outOfArea,
        tracking: data.tracking,
      } as never)
      .select("id")
      .single();

    if (insertErr) {
      console.error("[submitLead] failed to save lead to DB", insertErr);
      // Still attempt to forward to GHL — a DB hiccup should not lose the lead.
    }

    const leadId = (inserted as { id?: string } | null)?.id;
    const ghlOk = await ghlPromise;

    // 3. Update the lead row with forwarding status (best-effort).
    if (leadId) {
      await supabaseAdmin
        .from("leads" as never)
        .update({ ghl_forwarded: ghlOk, ghl_error: ghlError } as never)
        .eq("id", leadId);
    }

    // Always return success to the client — never block conversion on GHL.
    return { ok: true };
  });
